/* @HEADER
 * @version 1.1.959 | 2026-10-26
 * @purpose Decides what to do with a link given the person's right to see it now: waiting for the registry, no connection, one general unavailable for every refusal, or proceed; a copy of data on the device is never a right (ZVX-DEV-8263, R9 of the navigational-links PRD).
 * @sidecar navLinkResolve.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8330, ZVX-DEV-8263, ZVX-DEV-7134

/**
 * The decision half of opening a link (ZVX-4464): given an address and what the app can tell about the person's right to
 * see it NOW, say what to do. Nothing here touches storage, the network or the screen -- every question is a port --
 * so each of the four refusals of R9 (docs/prd/navigational-links.md) can be tested by answering the ports.
 *
 * THE RULE THIS FILE SERVES (the owner, 2026-10-24): a link works only for a person with enough rights to see what it leads
 * to. The right is read when the link is opened, and a copy of the data on the device is not a right -- a device keeps what
 * it once received. So:
 *
 *   - Nothing is judged before the project registry has answered (`registry` is `asking`): the list of projects on the
 *     device is as fresh as the last answer, and a person removed since still has the project in it. This holds for a
 *     project that is on the device too.
 *   - If the registry could not be asked at all (`no-answer`) the list is not trusted either: `no-connection`, with a retry.
 *   - The project must be in the list the app shows to this account (`projects`, the app's own `getAllProjects()` -- it
 *     drops a project proven foreign to the account and one being deleted; a project deleted elsewhere is absent after the
 *     registry's cycle). Not there: `unavailable`.
 *   - A space, and a board or card in it, must be one the server says the person sees now (`spaceRight`). The parent space
 *     is asked, never the board: the server closes a board or a card only through its space. An answer that did not come
 *     is `no-connection`; one that says no is `unavailable`.
 *
 * `unavailable` is ONE answer for every refusal and it carries nothing: no name of the entity, the space or the project,
 * and no hint which of the cases it was. The link opens nothing by itself and gives no access; it only leads.
 *
 * What this file does NOT decide: that a card is private to somebody else (`isCardVisibleInSpace`, which needs the
 * project's database and so runs after the project is switched to), and that the space is already on the device. Those
 * are a second stage that follows the switch of project and is not in this file.
 */

import type { NavLink } from './navLinks.ts';

/** The state of the project registry's first cycle of this session, as the app reads it. */
export type RegistryAnswer =
  /** The first cycle has not finished: ask again when it does. */
  | 'asking'
  /** It finished and no pull succeeded: the list on the device was not confirmed. */
  | 'no-answer'
  /** It finished with an answer: the list is as fresh as the registry's cycle. */
  | 'answered';

/** What the resolver needs to know about a project of the list. */
export interface LinkProject {
  /** The local key (names the project's database). */
  id: string;
  /** The server's id, once the project has been pushed; the address carries this one. */
  serverProjectId?: string | null;
  /** The organisation the project belongs to; `null` when the app cannot say. */
  organisationId: string | null;
}

/** The server's answer to «does this person see this space now». */
export type SpaceRight = 'granted' | 'refused' | 'unreachable';

export interface NavLinkPorts {
  registry(): RegistryAnswer;
  /**
   * ZVX-DEV-8353 (ZVX-4685): choose the organisation the project of an opened link belongs to, the way the workspace switcher would, so the shell
   * does not put its «out of scope» placard over the page of a project of another organisation. Returns whether it chose one. The judge never
   * calls it; the caller does, after a verdict of `proceed`. Absent: nothing to choose.
   */
  selectOrganisation?(organisationId: string): boolean;
  /**
   * Has the account's identity on this device been read yet (ZVX-DEV-8330, ZVX-4685). The organisation of a project is read from it; before it
   * is written a project names no organisation because the question cannot be answered, not because there is none. Absent: read.
   */
  identityRead?(): boolean;
  /** The projects the app shows to this account. Not the databases on the device: the LIST. */
  projects(): readonly LinkProject[];
  activeProjectId(): string;
  /** Does the server let this person see the space now. */
  spaceRight(ask: { organisationId: string; projectServerId: string | null; spaceId: string }): Promise<SpaceRight>;
}

export type NavLinkVerdict =
  | { kind: 'waiting' }
  | { kind: 'no-connection' }
  | { kind: 'unavailable' }
  | { kind: 'proceed'; project: LinkProject; switchProject: boolean };

const UNAVAILABLE: NavLinkVerdict = { kind: 'unavailable' };

/**
 * WHY a judgement ended as it did (ZVX-DEV-8330). Internal: it never reaches the screen -- the notice says the same general thing for every
 * refusal, on purpose (the owner, 2026-10-24) -- and carries no name or address. It goes to a `trace` the caller hands in, which the app turns
 * into a debug line, so the next complaint of «no access» can say which of these it was. `switch-not-taken` is the one the hook adds
 * itself (the judge said to switch project, and the switch was not possible or had already been tried for this very link).
 */
export type NavLinkWhy =
  | 'registry-asking'
  | 'registry-no-answer'
  | 'organisation-address'
  | 'project-absent'
  | 'project-ambiguous'
  | 'organisation-unread'
  | 'organisation-unknown'
  | 'space-refused'
  | 'space-unreachable'
  | 'switch-not-taken'
  | 'proceed';

/** The project of the list an address names: by its server id or by its local key. `null` when none, or when it is ambiguous. */
function projectNamed(projects: readonly LinkProject[], projectId: string): { project: LinkProject | null; why: NavLinkWhy } {
  const named = projects.filter((p) => p.serverProjectId === projectId || p.id === projectId);
  if (named.length === 1) return { project: named[0]!, why: 'proceed' };
  return { project: null, why: named.length === 0 ? 'project-absent' : 'project-ambiguous' };
}

export async function judgeNavLink(link: NavLink, ports: NavLinkPorts, trace: (why: NavLinkWhy) => void = () => {}): Promise<NavLinkVerdict> {
  const registry = ports.registry();
  if (registry === 'asking') return (trace('registry-asking'), { kind: 'waiting' });
  if (registry === 'no-answer') return (trace('registry-no-answer'), { kind: 'no-connection' });

  // An organisation has an address and no screen of its own to arrive on yet: it opens nothing, and says so as everything
  // else that cannot be opened does.
  if (link.kind === 'organisation') return (trace('organisation-address'), UNAVAILABLE);

  const named = projectNamed(ports.projects(), link.projectId);
  const project = named.project;
  if (project === null) return (trace(named.why), UNAVAILABLE);

  if (link.kind !== 'project') {
    // Whose server decides: the project's organisation. Not knowing it is not a reason to open -- and, until the identity it is read from has
    // been read, not a reason to refuse either (ZVX-DEV-8330): that is a question not yet answerable, so the link waits.
    if (project.organisationId === null) {
      if (ports.identityRead && !ports.identityRead()) return (trace('organisation-unread'), { kind: 'waiting' });
      return (trace('organisation-unknown'), UNAVAILABLE);
    }
    let right: SpaceRight;
    try {
      right = await ports.spaceRight({
        organisationId: project.organisationId,
        projectServerId: project.serverProjectId ?? null,
        spaceId: link.spaceId,
      });
    } catch {
      // A port that throws has not answered; it is not a refusal and it is not a grant.
      return { kind: 'no-connection' };
    }
    if (right === 'unreachable') return (trace('space-unreachable'), { kind: 'no-connection' });
    if (right !== 'granted') return (trace('space-refused'), UNAVAILABLE);
  }

  trace('proceed');
  return { kind: 'proceed', project, switchProject: project.id !== ports.activeProjectId() };
}
